import React, { useEffect, useState } from "react";
import { Twitter, X, Send, Loader2, CheckCircle2, Sparkles, Edit3, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/api/client";

const CHAR_LIMIT = 280;

/**
 * Preview + approve modal for OAuth 2.0 auto-tweets.
 * Shown right after an article is published (via the `pending_tweet` field in the publish response)
 * OR on page-load if any tweets are queued (loaded via /social/tweet/pending).
 *
 * Actions:
 *   - Approve → POST /api/social/tweet/scheduled/{id}/approve  (posts immediately)
 *   - Save & approve → PATCH edited text, then approve
 *   - Cancel → DELETE
 *   - Close (X) → keeps the tweet queued; user can approve later from a future banner
 */
export default function TweetPreviewModal({ pendingTweet, onClose, onApproved, showToast }) {
  const [tweet, setTweet] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (pendingTweet) {
      setTweet(pendingTweet.tweet || "");
      setDirty(false);
    }
  }, [pendingTweet]);

  if (!pendingTweet) return null;
  const remaining = CHAR_LIMIT - tweet.length;
  const overLimit = remaining < 0;

  const pickVariant = (text) => {
    if (!text) return;
    // Rebuild with the existing trailing link + hashtags if present
    const link = pendingTweet.trackable_link ? ` ${pendingTweet.trackable_link}` : "";
    const tags = (pendingTweet.hashtags || []).slice(0, 2).map((h) => `#${String(h).replace(/^#/, "")}`).join(" ");
    const tail = tags ? ` ${tags}` : "";
    let composed = `${text}${link}${tail}`;
    if (composed.length > CHAR_LIMIT) composed = `${text}${link}`;
    setTweet(composed);
    setDirty(true);
  };

  const approve = async () => {
    if (overLimit) return;
    setBusy(true);
    try {
      if (dirty) {
        await api.patch(`/social/tweet/scheduled/${pendingTweet.id}`, { tweet });
      }
      const { data } = await api.post(`/social/tweet/scheduled/${pendingTweet.id}/approve`);
      onApproved?.(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Failed to post";
      showToast?.(msg, "err");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Discard this tweet? The article stays published — only the tweet is dropped.")) return;
    setBusy(true);
    try {
      await api.delete(`/social/tweet/scheduled/${pendingTweet.id}`);
      showToast?.("Tweet discarded.");
      onClose?.();
    } catch (e) {
      showToast?.(formatApiErrorDetail(e.response?.data?.detail) || "Failed", "err");
    } finally {
      setBusy(false);
    }
  };

  const variants = [
    { key: "primary", label: "Primary", text: pendingTweet.primary },
    { key: "alt_1", label: "Alt 1", text: pendingTweet.alt_1 },
    { key: "alt_2", label: "Alt 2", text: pendingTweet.alt_2 },
  ].filter((v) => v.text);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tweet-preview-backdrop"
    >
      <div
        className="bg-slate-950 border border-slate-800 rounded-xl max-w-xl w-full"
        onClick={(e) => e.stopPropagation()}
        data-testid="tweet-preview-modal"
      >
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Twitter className="w-5 h-5 text-sky-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">
                Ready to tweet · needs your approval
              </p>
              <h3 className="font-serif text-lg text-sky-100 truncate">
                {pendingTweet.article_title || "New article"}
              </h3>
            </div>
          </div>
          <button
            data-testid="btn-tweet-preview-close"
            onClick={onClose}
            className="text-slate-500 hover:text-sky-300"
            title="Keep queued — decide later"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Twitter-card preview */}
        <div className="p-5">
          <div className="border border-sky-500/30 bg-slate-900/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-sky-400 text-slate-950 flex items-center justify-center font-serif text-lg flex-shrink-0">
                <Twitter className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sky-100">You</p>
                <p className="text-[11px] text-slate-500 font-mono">via AllBusinessOnline · now</p>
              </div>
            </div>
            <textarea
              data-testid="tweet-preview-textarea"
              value={tweet}
              onChange={(e) => { setTweet(e.target.value); setDirty(true); }}
              rows={5}
              className="w-full bg-transparent text-sky-50 text-sm mt-3 outline-none resize-none border-none focus:ring-0"
              placeholder="Your tweet…"
              disabled={busy}
            />
          </div>

          <div className="flex items-center justify-between mt-2">
            <p
              data-testid="tweet-preview-count"
              className={`text-[11px] font-mono ${overLimit ? "text-red-400" : remaining < 20 ? "text-amber-400" : "text-slate-500"}`}
            >
              {tweet.length} / {CHAR_LIMIT} · {remaining >= 0 ? `${remaining} left` : `${Math.abs(remaining)} over`}
            </p>
            {dirty && <span className="text-[10px] font-mono text-sky-300">edited</span>}
          </div>

          {/* Variant picker (if available) */}
          {variants.length > 1 && (
            <div className="mt-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Try another angle
              </p>
              <div className="space-y-1.5">
                {variants.map((v) => (
                  <button
                    key={v.key}
                    data-testid={`tweet-variant-${v.key}`}
                    onClick={() => pickVariant(v.text)}
                    disabled={busy}
                    className="w-full text-left px-3 py-2 rounded border border-slate-800 hover:border-sky-500/40 hover:bg-sky-500/5 text-xs text-slate-300"
                  >
                    <span className="font-mono text-[10px] uppercase text-sky-400 mr-2">{v.label}</span>
                    {v.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Hashtag chips */}
          {(pendingTweet.hashtags || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(pendingTweet.hashtags || []).map((h) => (
                <span
                  key={h}
                  data-testid={`tweet-hashtag-${h}`}
                  className="text-[10px] font-mono px-2 py-0.5 rounded border border-sky-500/30 text-sky-300 bg-sky-500/5"
                >
                  #{String(h).replace(/^#/, "")}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-5 border-t border-slate-800 flex items-center justify-between gap-3">
          <button
            data-testid="btn-tweet-cancel"
            onClick={cancel}
            disabled={busy}
            className="px-3 py-2 text-red-300 hover:bg-red-500/10 rounded text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Discard
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-tweet-later"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 border border-slate-700 text-slate-400 hover:text-sky-300 hover:border-sky-500/40 rounded text-sm"
              title="Keep in queue — approve later"
            >
              Later
            </button>
            <button
              data-testid="btn-tweet-approve"
              onClick={approve}
              disabled={busy || overLimit || tweet.trim().length === 0}
              className="px-5 py-2 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                dirty ? <><Edit3 className="w-4 h-4" /> Save & post</> :
                        <><Send className="w-4 h-4" /> Post to X</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
